/**
 * Phase 5.4 — Due Out risk eligibility helper.
 *
 * Doctrine:
 *   - Bucket 4.5 §4.5.1  Due Out is a hard customer-promise constraint.
 *   - Bucket 4.5 §4.5.2  Pickup delay after on-time completion is NOT a
 *                        production Due Out breach.
 *   - Bucket 4.5 §4.5.6  Due Out applies WHILE production work is
 *                        incomplete.
 *   - Bucket 4.5 §4.5.9  Held-in-Parking / Scheduled-but-Parked tickets
 *                        remain Due-Out relevant even though parked.
 *   - Bucket 3 §3.2      Physical location does not erase Due Out risk.
 *   - Bucket 3 §3.4      Production states drive lifecycle semantics.
 *
 * Why this module exists
 * ----------------------
 * Phase 5.3 added the corrective-action vocabulary for Due Out misses.
 * Phase 5.4 closes the matching detection-side question: WHICH tickets
 * are Due-Out risk relevant in the first place?
 *
 * The existing edge-function `detectDueOutMisses` (in
 * `supabase/functions/_shared/boardIssueDetector.ts`) requires the
 * trinity `dueOut + startTime + assignedTechId` and is gated by an
 * upstream `status !== 'Completed' && status !== 'Deleted'` filter.
 * That happens to be doctrinally correct for the Bucket 3 / Bucket 4.5
 * cases because the production-state cases that should be excluded
 * (parking_lot_inactive, estimate_prospect, picked_up,
 * completed_awaiting_pickup, deleted, archived) are EACH already
 * filtered by either the status check OR the trinity requirement
 * (e.g., parking_lot_inactive has no `assignedTechId`).
 *
 * Phase 5.4 ELEVATES that coincidence to an explicit canonical helper.
 * Callers no longer need to know which combination of status + columns
 * implies "scan this for Due Out"; they ask the helper.
 *
 * Phase 5.4 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock, no React hooks. Composes Phase 1.1's
 *     `getProductionState` and Phase 1.3's `isDueOutRelevantState`.
 *   - Does NOT modify `boardIssueDetector`, `detectDueOutMisses`,
 *     `DueOutMissResolver`, `DueOutCorrectiveActionPlanner`, OptiBoard,
 *     Parking Lot, Birdseye, cascade, AutoBump, or any DB schema.
 *   - Adoption (Phase 5.5+) wires the helper into the detector so the
 *     filter is explicit instead of implicit-via-data-invariants.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState`.
 *   - Phase 1.3 `isProductionRelevantState` / `isDueOutRelevantState`
 *     (same set in practice; `isDueOutRelevantState` is the doctrinal
 *     name for this set per Bucket 4.5 §4.5.6).
 *   - Phase 5.1 — re-promise / breach-record paths consume this helper
 *     to determine whether a Due Out edit applies to an active risk.
 */

import {
  getProductionState,
  isDueOutRelevantState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Type model
// =============================================================================

/**
 * Discrete reason codes for why a ticket is NOT Due-Out risk relevant.
 * `null` is returned by `getDueOutRiskExclusionReason` when the ticket
 * IS risk relevant (no exclusion). Exhaustive over the production-state
 * union, plus a few non-state structural reasons.
 *
 *   - 'production_state_terminal_deleted'      — `getProductionState === 'deleted'`
 *   - 'production_state_terminal_archived'     — `getProductionState === 'archived'`
 *   - 'production_state_picked_up'             — vehicle has been picked up
 *   - 'production_state_completed_awaiting_pickup'
 *                                              — production is complete; pickup
 *                                                delay is NOT a Due Out breach
 *                                                (Bucket 4.5 §4.5.2)
 *   - 'production_state_parking_lot_inactive'  — Bucket 3 §3.4.11 — no
 *                                                active dispatch plan
 *   - 'production_state_estimate_prospect'     — not yet scheduled work
 *   - 'cancelled_status'                       — `status === 'Cancelled'`,
 *                                                doctrinal exclusion regardless
 *                                                of production-state resolution
 *   - 'no_due_out_set'                         — `due_out` is null/undefined,
 *                                                so there is no promise to
 *                                                breach
 */
export type DueOutRiskExclusionReason =
  | 'production_state_terminal_deleted'
  | 'production_state_terminal_archived'
  | 'production_state_picked_up'
  | 'production_state_completed_awaiting_pickup'
  | 'production_state_parking_lot_inactive'
  | 'production_state_estimate_prospect'
  | 'cancelled_status'
  | 'no_due_out_set';

/**
 * Permissive ticket shape for Due Out risk eligibility. Extends the
 * Phase 1.1 production-state shape with the additional fields the
 * helper inspects directly.
 */
export interface DueOutRiskTicketShape extends ProductionStateTicketShape {
  /** Customer-promise time. Null/missing ⇒ no risk to track. */
  due_out?: string | Date | null;
}

// =============================================================================
// Public predicates
// =============================================================================

/**
 * Is this production state Due-Out risk relevant?
 *
 * Pure state-level predicate. Bucket 4.5 §4.5.6: Due Out applies WHILE
 * production work is incomplete. The risk-relevant set is exactly the
 * Phase 1.3 `isDueOutRelevantState` set (= `isProductionRelevantState`):
 *
 *   - `scheduled`             — Bucket 3 §3.4.2
 *   - `scheduled_but_parked`  — Bucket 3 §3.4.3 (parking does not erase risk)
 *   - `active_in_bay`         — Bucket 3 §3.4.4
 *   - `held_in_bay`           — Bucket 3 §3.4.5
 *   - `held_in_parking`       — Bucket 3 §3.4.6 (parked, but still incomplete)
 *
 * NOT risk relevant:
 *   - `deleted`, `archived`            — terminal
 *   - `picked_up`                      — customer lifecycle terminal
 *   - `completed_awaiting_pickup`      — production complete (§4.5.2)
 *   - `parking_lot_inactive`           — no active dispatch plan (§3.4.11)
 *   - `estimate_prospect`              — not yet scheduled work (§3.4.1)
 */
export function isDueOutRiskRelevantProductionState(state: ProductionState): boolean {
  return isDueOutRelevantState(state);
}

/**
 * Is this ticket currently Due-Out risk relevant?
 *
 * Returns `true` iff the ticket has a `due_out` value, is NOT in a
 * `'Cancelled'` status, AND its production state is risk-relevant.
 *
 * Composes:
 *   1. structural check — `due_out` must be set
 *   2. status check     — Bucket 4.5: cancelled work has no promise to breach
 *   3. production-state check via `getProductionState` (Phase 1.1)
 *
 * Pure: no DB calls, no clock reads.
 */
export function isTicketDueOutRiskRelevant(ticket: DueOutRiskTicketShape): boolean {
  return getDueOutRiskExclusionReason(ticket) === null;
}

/**
 * Return the specific reason this ticket is NOT Due-Out risk relevant.
 * Returns `null` when the ticket IS risk relevant.
 *
 * Useful for diagnostics, audit rows, and UI tooltips that want to show
 * exactly WHY a ticket isn't being scanned for Due Out misses.
 *
 * Resolution order (first match wins):
 *   1. `due_out` is null/missing            ⇒ 'no_due_out_set'
 *   2. `status === 'Cancelled'`              ⇒ 'cancelled_status'
 *   3. production-state-specific exclusions  (`production_state_*` codes)
 *   4. production state is risk-relevant    ⇒ null (no exclusion)
 */
export function getDueOutRiskExclusionReason(
  ticket: DueOutRiskTicketShape,
): DueOutRiskExclusionReason | null {
  if (ticket.due_out == null) return 'no_due_out_set';
  if (ticket.status === 'Cancelled') return 'cancelled_status';

  const state = getProductionState(ticket);
  switch (state) {
    case 'deleted':
      return 'production_state_terminal_deleted';
    case 'archived':
      return 'production_state_terminal_archived';
    case 'picked_up':
      return 'production_state_picked_up';
    case 'completed_awaiting_pickup':
      return 'production_state_completed_awaiting_pickup';
    case 'parking_lot_inactive':
      return 'production_state_parking_lot_inactive';
    case 'estimate_prospect':
      return 'production_state_estimate_prospect';
    case 'scheduled':
    case 'scheduled_but_parked':
    case 'active_in_bay':
    case 'held_in_bay':
    case 'held_in_parking':
      // Bucket 4.5 §4.5.9 — physical parking does NOT erase Due Out risk.
      // Bucket 3 §3.2 — physical location does not determine production state.
      return null;
  }
}

/**
 * Convenience: should this ticket be SCANNED by `detectDueOutMisses`
 * (or any sibling scanner) for active Due Out risk?
 *
 * Sister to `isTicketDueOutRiskRelevant` — semantic alias intended for
 * adoption inside detector pre-filters.
 *
 *   const activeTickets = boardState.tickets.filter(shouldScanTicketForDueOutRisk);
 *
 * Adoption note: the current edge-function `detectBoardIssues` uses a
 * narrower pre-filter (`status !== 'Completed' && status !== 'Deleted'`)
 * combined with the trinity requirement in `detectDueOutMisses`
 * (`dueOut + startTime + assignedTechId`). That combination is
 * doctrinally correct today but implicit. A Phase 5.5 adoption can
 * replace the combination with this single helper for an explicit
 * canonical gate.
 */
export function shouldScanTicketForDueOutRisk(
  ticket: DueOutRiskTicketShape,
): boolean {
  return isTicketDueOutRiskRelevant(ticket);
}
